type ConfigObject = Record<string, unknown>;

const isConfigObject = (value: unknown): value is ConfigObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeBySchema = (current: unknown, schema: unknown): unknown => {
  if (Array.isArray(schema)) {
    return Array.isArray(current) ? current : schema;
  }
  if (!isConfigObject(schema)) {
    return current !== undefined ? current : schema;
  }

  const result: ConfigObject = {};
  Object.entries(schema).forEach(([key, schemaValue]) => {
    const currentValue = isConfigObject(current) && Object.prototype.hasOwnProperty.call(current, key)
      ? current[key]
      : undefined;
    result[key] = normalizeBySchema(currentValue, schemaValue);
  });
  return result;
};

const normalizeSymbolConfig = (
  currentConfig: unknown,
  schemaConfig: unknown,
  symbols: unknown[],
): unknown => {
  if (!isConfigObject(schemaConfig)) return currentConfig || {};

  const schemaSymbols = Object.keys(schemaConfig);
  const template = schemaSymbols.length > 0 ? schemaConfig[schemaSymbols[0]] : {};
  const targetSymbols = symbols.length > 0 ? symbols.map(String) : schemaSymbols;
  const currentBySymbol = isConfigObject(currentConfig) ? currentConfig : {};

  return targetSymbols.reduce<ConfigObject>((result, symbol) => {
    const symbolSchema = schemaConfig[symbol] || template;
    result[symbol] = normalizeBySchema(currentBySymbol[symbol], symbolSchema);
    return result;
  }, {});
};

const normalizePositionSizes = (
  currentSizes: unknown,
  schemaSizes: unknown,
  symbols: unknown[],
): unknown => {
  if (!isConfigObject(schemaSizes)) return currentSizes || {};

  const targetSymbols = symbols.length > 0 ? symbols.map(String) : Object.keys(schemaSizes);
  const currentBySymbol = isConfigObject(currentSizes) ? currentSizes : {};
  return targetSymbols.reduce<ConfigObject>((result, symbol) => {
    if (Object.prototype.hasOwnProperty.call(currentBySymbol, symbol)) {
      result[symbol] = currentBySymbol[symbol];
    } else if (Object.prototype.hasOwnProperty.call(schemaSizes, symbol)) {
      result[symbol] = schemaSizes[symbol];
    }
    return result;
  }, {});
};

export const normalizeStrategyConfigForFormat = (
  current: unknown,
  schema: unknown,
): unknown => {
  if (!isConfigObject(schema)) return current;

  const normalized = normalizeBySchema(current, schema) as ConfigObject;
  const symbols = Array.isArray(normalized.symbols)
    ? normalized.symbols
    : (Array.isArray(schema.symbols) ? schema.symbols : []);
  const currentConfig = isConfigObject(current) ? current : {};

  if (schema.symbol_config) {
    normalized.symbol_config = normalizeSymbolConfig(
      currentConfig.symbol_config,
      schema.symbol_config,
      symbols,
    );
  }
  if (schema.position_sizes) {
    normalized.position_sizes = normalizePositionSizes(
      currentConfig.position_sizes,
      schema.position_sizes,
      symbols,
    );
  }

  return normalized;
};
